/**
 * FlowV2 debug launch gate and follower role.
 * Launch (Start from stopped, Step from stopped, Wait For Next) waits until the
 * shared edit generation is saved and the latest code validation has no errors.
 */

export const CODE_CHANGING_EVENTS = new Set([
    'Add',
    'Remove',
    'UpdateArgs',
    'UpdateOutput',
    'UpdateMatchPorts',
    'AddLink',
    'UpdateLink',
    'RemoveLink',
    'AddLocal',
    'RemoveLocal',
    'UpdateLocal',
    'SettingsChanged',
    'Delta',
    'Undo',
    'Redo',
]);

export function freshDebugGateState() {
    return {
        autoSaved: 'SAVED',
        validationPending: true,
        validationErrorCount: 0,
        requiredCodeVersion: null,
        acceptNextValidation: false,
        awaitingDirty: false,
        seenEditGeneration: 0,
        lastCodeVersion: null,
        debugMode: 'stopped',
        debugLaunching: false,
        debugLocked: false,
        lockedProjectIds: [],
        isDebugOwner: false,
        debugRole: '',
    };
}

function ownerViewingOutsideLock(state) {
    const ids = state.lockedProjectIds;
    if (!state.isDebugOwner || state.debugMode === 'stopped')
        return false;
    if (!Array.isArray(ids) || ids.length === 0)
        return false;
    return !state.debugLocked;
}

function generationOf(value) {
    if (value == null || value === '')
        return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
}

export function countValidationErrors(content) {
    if (Array.isArray(content))
        return content.length;
    if (content && typeof content === 'object')
        return Object.keys(content).length;
    return 0;
}

export function debugGateSnapshot(state) {
    return {
        autoSaved: state.autoSaved,
        validationPending: !!state.validationPending,
        validationErrorCount: state.validationErrorCount || 0,
        requiredCodeVersion: state.requiredCodeVersion ?? null,
        acceptNextValidation: !!state.acceptNextValidation,
        awaitingDirty: !!state.awaitingDirty,
        seenEditGeneration: state.seenEditGeneration || 0,
        lastCodeVersion: state.lastCodeVersion ?? null,
    };
}

/** A flow that already saved and finished validation can be shown again without waiting for a compile that will not be repeated. */
export function reusableDebugGate(snapshot) {
    if (!snapshot || snapshot.autoSaved !== 'SAVED')
        return null;
    if (snapshot.validationPending || snapshot.awaitingDirty)
        return null;
    return snapshot;
}

export function applyMethodOpened(state) {
    state.autoSaved = 'SAVED';
    state.validationPending = true;
    state.validationErrorCount = 0;
    state.requiredCodeVersion = null;
    state.acceptNextValidation = false;
    state.awaitingDirty = false;
    state.seenEditGeneration = 0;
    state.lastCodeVersion = null;
    return state;
}

export function applyLocalEdit(state, { codeChanged } = {}) {
    state.autoSaved = 'SAVING';
    if (codeChanged) {
        state.validationPending = true;
        state.awaitingDirty = true;
        state.requiredCodeVersion = null;
    }
    return state;
}

export function applyDirtyNotice(state, notice) {
    const gen = generationOf(notice?.editGeneration);
    if (gen != null)
        state.seenEditGeneration = Math.max(state.seenEditGeneration || 0, gen);
    state.autoSaved = 'SAVING';
    state.awaitingDirty = false;
    if (notice?.codeChanged) {
        state.validationPending = true;
        const version = generationOf(notice?.codeVersion);
        if (version != null)
            state.requiredCodeVersion = version;
    }
    if (notice?.forceValidation) {
        state.validationPending = true;
        state.acceptNextValidation = true;
    }
    return state;
}

export function applyValidationNotice(state, notice) {
    const version = generationOf(notice?.codeVersion);
    const errors = notice?.errorCount != null
        ? Number(notice.errorCount) || 0
        : countValidationErrors(notice?.errors);
    if (state.awaitingDirty && state.requiredCodeVersion == null && !state.acceptNextValidation)
        return state;
    if (state.acceptNextValidation) {
        state.acceptNextValidation = false;
        state.validationPending = false;
        state.awaitingDirty = false;
        state.validationErrorCount = errors;
        if (version != null)
            state.lastCodeVersion = version;
        return state;
    }
    if (state.requiredCodeVersion != null && (version == null || version < state.requiredCodeVersion))
        return state;
    state.validationPending = false;
    state.awaitingDirty = false;
    state.validationErrorCount = errors;
    if (version != null)
        state.lastCodeVersion = version;
    return state;
}

export function applySavedNotice(state, notice) {
    const gen = generationOf(notice?.editGeneration);
    if (gen == null || gen >= (state.seenEditGeneration || 0))
        state.autoSaved = 'SAVED';
    return state;
}

/**
 * A lock that arrived while the flow document was still loading must be kept.
 * Drop the open project only when this subscribe never confirmed a lock for it.
 */
export function resolveSubscribeDebugLock({ seen, openProjectId, projectIds }) {
    const ids = Array.isArray(projectIds) ? projectIds : [];
    const matched = !!openProjectId && ids.some(id => id == openProjectId);
    if (seen || matched)
        return { dropOpenProject: false, seen: true };
    return { dropOpenProject: true, seen: false };
}

function normalizeIdList(raw) {
    if (Array.isArray(raw))
        return raw.map(id => '' + id).filter(Boolean);
    if (raw && typeof raw === 'object')
        return Object.keys(raw).map(key => raw[key]).map(id => '' + id).filter(id => id && id !== '[object Object]');
    if (raw != null && raw !== '')
        return ['' + raw];
    return [];
}

/**
 * A DebugLock names every locked project and the flow whose editor is being notified.
 * The open designer is locked when that flow is the one on screen, or when its project is in the list.
 * A matching flow pulls the open project into the list so a later scope check still sees it.
 */
export function mergeDebugLockNotice({ openFlowId, openProjectId, flowId, projectIds }) {
    const locked = normalizeIdList(projectIds);
    const forOpenFlow = !!openFlowId && !!flowId && '' + flowId == '' + openFlowId;
    if (forOpenFlow && openProjectId && !locked.some(id => id == openProjectId))
        locked.push('' + openProjectId);
    const projectMatch = !!openProjectId && locked.some(id => id == openProjectId);
    return {
        applies: forOpenFlow || projectMatch,
        forOpenFlow,
        projectIds: locked,
    };
}

export function applyDebugLockScope(state, scope) {
    const ids = Array.isArray(scope?.projectIds) ? scope.projectIds : [];
    const open = scope?.openProjectId;
    const inside = !!open && ids.some(id => id == open);
    state.debugLocked = inside;
    if (!inside && state.debugRole === 'follower')
        state.debugRole = '';
    if (inside && scope?.isOwner) {
        state.isDebugOwner = true;
        if (state.debugRole !== 'follower')
            state.debugRole = 'owner';
    }
    return state;
}

/**
 * When Step Into / Call Stack opens a method in another project, keep that project
 * in the owner's lock set so ownerViewingOutsideLock does not disable Step/Resume.
 * Manual folder navigation away still leaves the set and drops drive chrome.
 */
export function expandLockedProjectIds(projectIds, projectId) {
    const ids = normalizeIdList(projectIds);
    if (projectId == null || projectId === '')
        return ids;
    const id = '' + projectId;
    if (!ids.some(x => x == id))
        ids.push(id);
    return ids;
}

export function applyRejectedEdit(state) {
    state.autoSaved = 'SAVED';
    state.validationPending = false;
    state.awaitingDirty = false;
    state.requiredCodeVersion = null;
    state.acceptNextValidation = false;
    return state;
}

export function canDriveDebug(state) {
    if (state.debugRole === 'follower')
        return false;
    if (state.debugLocked && !state.isDebugOwner)
        return false;
    if (ownerViewingOutsideLock(state))
        return false;
    return true;
}

export function canLaunchNewDebugSession(state) {
    if (!canDriveDebug(state))
        return false;
    if (state.debugLaunching)
        return false;
    const mode = state.debugMode;
    if (mode === 'starting' || mode === 'running' || mode === 'paused')
        return false;
    if (state.autoSaved !== 'SAVED')
        return false;
    if (state.validationPending)
        return false;
    if ((state.validationErrorCount || 0) > 0)
        return false;
    return true;
}

export function launchBlockReason(state) {
    if (state.debugRole === 'follower')
        return 'Following a debug session';
    if (state.debugLocked && !state.isDebugOwner)
        return 'Debugging in progress';
    if (state.debugLaunching || state.debugMode === 'starting' || state.debugMode === 'running' || state.debugMode === 'paused')
        return '';
    if (state.autoSaved === 'FAILED')
        return 'Save failed';
    if (state.autoSaved !== 'SAVED')
        return 'Save and validate before debugging';
    if (state.validationPending)
        return 'Waiting for validation';
    if ((state.validationErrorCount || 0) > 0)
        return 'Fix validation errors before debugging';
    return '';
}

export function canResumeOrStep(state) {
    if (!canDriveDebug(state))
        return false;
    if (state.debugLaunching)
        return false;
    if (state.debugMode === 'paused')
        return true;
    if (state.debugMode === 'stopped')
        return canLaunchNewDebugSession(state);
    return false;
}

export function canPauseDebug(state) {
    if (!canDriveDebug(state))
        return false;
    return state.debugMode === 'running' || state.debugMode === 'starting';
}

/** Property editors stay locked for the whole live session, not only during launch or a foreign lock. */
export function editorShouldBeReadOnly(state) {
    if (state?.debugLaunching || state?.debugLocked)
        return true;
    const mode = state?.debugMode;
    return mode === 'starting' || mode === 'running' || mode === 'paused';
}

export function canStopDebug(state) {
    if (state.debugRole === 'follower')
        return false;
    if (state.isDebugOwner && (state.debugMode !== 'stopped' || !!state.debugLaunching))
        return true;
    if (!canDriveDebug(state))
        return false;
    return state.debugMode !== 'stopped' || !!state.debugLaunching;
}

export function canToggleBreakpoints(state) {
    return canDriveDebug(state);
}

export function canFollowDebug(state) {
    return !!state.debugLocked && !state.isDebugOwner && state.debugRole !== 'follower';
}

export function startButtonTip(state) {
    if (state.debugMode === 'paused')
        return canDriveDebug(state) ? 'Resume' : (launchBlockReason(state) || 'Debugging in progress');
    return launchBlockReason(state) || 'Start Session';
}

export function stepButtonTip(state, label) {
    if (!canResumeOrStep(state))
        return launchBlockReason(state) || 'Debugging in progress';
    return label;
}

/** A stop started by the call leg dropping, or one that lost the race with Trace ended, is not a failure. */
export function shouldReportDebugStopFailure(options) {
    if (options?.quiet)
        return false;
    if (options?.debugMode === 'stopped')
        return false;
    return true;
}

/** API rejections are often response objects with no Error.message. */
export function formatDebugStopError(err) {
    if (err == null)
        return 'Unknown error';
    if (typeof err === 'string')
        return err || 'Unknown error';
    const message = err.message || err.Message;
    if (message)
        return String(message);
    const body = err.ResponseBody ?? err.responseBody;
    if (typeof body === 'string' && body)
        return body;
    const status = err.status || err.StatusCode || err.statusCode;
    if (status)
        return 'HTTP ' + status;
    return 'Unknown error';
}
